import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { updatePassword, updateUserAttribute, confirmUserAttribute, sendUserAttributeVerificationCode, fetchAuthSession } from 'aws-amplify/auth';
import { api } from '../lib/api';
import { useAuth } from '../components/context/AuthContext';
import { getCurrentUserWithClinic } from '../lib/getCurrentUserWithClinic';
import { Check, X, User, Building2, Users, Loader2, Send, BarChart3, Trash2, Mail, Lock, Briefcase, MapPin, Shield } from 'lucide-react';

const baseTabs = [
  { id: 'profile', label: 'Profile', icon: User },
  { id: 'organization', label: 'Organization', icon: Building2 },
  { id: 'team', label: 'Team', icon: Users },
];

// Shared UI components
const SectionCard = ({ children, className = '' }) => (
  <div className={`bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden ${className}`}>
    {children}
  </div>
);

const SectionHeader = ({ icon: Icon, title, description }) => (
  <div className="px-6 py-5 border-b border-slate-100">
    <div className="flex items-center gap-3">
      {Icon && (
        <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center">
          <Icon className="h-4 w-4 text-slate-600" />
        </div>
      )}
      <div>
        <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
        {description && <p className="text-xs text-slate-500 mt-0.5">{description}</p>}
      </div>
    </div>
  </div>
);

const SectionBody = ({ children }) => (
  <div className="px-6 py-5">{children}</div>
);

const SectionFooter = ({ children }) => (
  <div className="px-6 py-4 bg-slate-50/50 border-t border-slate-100 flex items-center justify-end gap-3">
    {children}
  </div>
);

const inputClasses = "w-full px-3.5 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-slate-900 focus:border-transparent outline-none transition-shadow bg-white";

export default function Settings() {
  const [activeTab, setActiveTab] = useState('profile');
  const [role, setRole] = useState(null);
  const [clinicId, setClinicId] = useState(null);

  useEffect(() => {
    async function fetchUserContext() {
      try {
        const { clinicId: cId, role: userRole } = await getCurrentUserWithClinic();
        setRole(userRole);
        setClinicId(cId);
      } catch (err) {
        // role remains null, Reports tab stays hidden
      }
    }
    fetchUserContext();
  }, []);

  const tabs = role === 'admin'
    ? [...baseTabs, { id: 'reports', label: 'Reports', icon: BarChart3 }]
    : baseTabs;

  return (
    <div className="max-w-4xl mx-auto">
      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900">Settings</h1>
        <p className="mt-1 text-sm text-slate-500">
          Manage your account, organization, and team settings.
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200 mb-6">
        <nav className="flex gap-6">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-1 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-slate-900 text-slate-900'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Content — no wrapper card, each section has its own card */}
      <div className="space-y-6">
        {activeTab === 'profile' && <ProfileSettings />}
        {activeTab === 'organization' && <OrganizationSettings />}
        {activeTab === 'team' && <TeamManagement />}
        {activeTab === 'reports' && <ReportsSettings clinicId={clinicId} />}
      </div>
    </div>
  );
}

function ProfileSettings() {
  const { refreshUser, profile } = useAuth();
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [profileEmail, setProfileEmail] = useState('');
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileSaving, setProfileSaving] = useState(false);
  const originalProfile = useRef({});

  // Email change flow
  const [newEmail, setNewEmail] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailVerifying, setEmailVerifying] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [emailStep, setEmailStep] = useState('display'); // 'display' | 'edit' | 'verify'

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    async function fetchProfile() {
      try {
        const p = await api.getCurrentProfile();
        setFullName(p.full_name || '');
        setPhone(p.phone || '');
        setJobTitle(p.job_title || '');
        setProfileEmail(p.email || '');
        originalProfile.current = {
          full_name: p.full_name || '',
          phone: p.phone || '',
          job_title: p.job_title || '',
        };
      } catch (err) {
        toast.error('Error loading profile');
      } finally {
        setProfileLoading(false);
      }
    }
    fetchProfile();
  }, []);

  const profileDirty =
    fullName !== originalProfile.current.full_name ||
    phone !== originalProfile.current.phone ||
    jobTitle !== originalProfile.current.job_title;

  const handleSaveProfile = async () => {
    setProfileSaving(true);
    try {
      await api.updateProfile({ full_name: fullName, phone, job_title: jobTitle });
      originalProfile.current = { full_name: fullName, phone, job_title: jobTitle };
      toast.success('Profile updated');
      refreshUser();
    } catch (err) {
      toast.error(err.message || 'Failed to update profile');
    } finally {
      setProfileSaving(false);
    }
  };

  const handleEmailChange = async () => {
    if (!newEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }
    if (newEmail === profileEmail) {
      toast.error('New email is the same as current email');
      return;
    }
    setEmailSending(true);
    try {
      const result = await updateUserAttribute({
        userAttribute: { attributeKey: 'email', value: newEmail },
      });
      if (result.nextStep?.updateAttributeStep === 'CONFIRM_ATTRIBUTE_WITH_CODE') {
        const destination = result.nextStep.codeDeliveryDetails?.destination || newEmail;
        toast.success(`Verification code sent to ${destination}`);
        setEmailStep('verify');
      } else if (result.isUpdated) {
        await fetchAuthSession({ forceRefresh: true });
        await api.syncProfileEmail();
        setProfileEmail(newEmail);
        setNewEmail('');
        setEmailStep('display');
        toast.success('Email updated successfully');
        refreshUser();
      }
    } catch (err) {
      toast.error(err.message || 'Failed to initiate email change');
    } finally {
      setEmailSending(false);
    }
  };

  const handleVerifyEmail = async () => {
    if (!verificationCode.trim()) {
      toast.error('Please enter the verification code');
      return;
    }
    setEmailVerifying(true);
    try {
      await confirmUserAttribute({ userAttributeKey: 'email', confirmationCode: verificationCode.trim() });
      await fetchAuthSession({ forceRefresh: true });
      await api.syncProfileEmail();
      setProfileEmail(newEmail);
      setNewEmail('');
      setVerificationCode('');
      setEmailStep('display');
      toast.success('Email updated successfully');
      refreshUser();
    } catch (err) {
      toast.error(err.message || 'Failed to verify email');
    } finally {
      setEmailVerifying(false);
    }
  };

  const handleResendCode = async () => {
    try {
      await sendUserAttributeVerificationCode({ userAttributeKey: 'email' });
      toast.success('Verification code resent');
    } catch (err) {
      toast.error(err.message || 'Failed to resend code');
    }
  };

  const handleCancelEmailChange = () => {
    setNewEmail('');
    setVerificationCode('');
    setEmailStep('display');
  };

  const passwordRules = {
    length: newPassword.length >= 8,
    uppercase: /[A-Z]/.test(newPassword),
    lowercase: /[a-z]/.test(newPassword),
    number: /[0-9]/.test(newPassword),
    specialChar: /[^A-Za-z0-9]/.test(newPassword),
  };

  const allRulesPassed = Object.values(passwordRules).every(Boolean);
  const passwordsMatch = newPassword === confirmPassword && newPassword !== '';

  const handleUpdatePassword = async () => {
    if (!newPassword || !confirmPassword) {
      toast.error('Please fill in all password fields.');
      return;
    }
    if (newPassword === currentPassword) {
      toast.error('New password must be different from your current password.');
      return;
    }
    if (!allRulesPassed) {
      toast.error('New password does not meet all requirements.');
      return;
    }
    if (!passwordsMatch) {
      toast.error('New password and confirm password do not match.');
      return;
    }
    setLoading(true);
    try {
      await updatePassword({ oldPassword: currentPassword, newPassword });
      toast.success('Password updated successfully.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast.error(err.message || 'Failed to update password.');
    } finally {
      setLoading(false);
    }
  };

  const RuleItem = ({ passed, text }) => (
    <li className={`flex items-center gap-2 text-sm ${passed ? 'text-emerald-600' : 'text-slate-400'}`}>
      {passed ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
      {text}
    </li>
  );

  // User initials for avatar
  const initials = (fullName || profileEmail || '?')
    .split(' ')
    .map(w => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  if (profileLoading) {
    return (
      <div className="space-y-6">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-32 bg-white rounded-xl border border-slate-200 animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Identity Card */}
      <SectionCard>
        <div className="px-6 py-6 flex items-center gap-5">
          <div className="w-16 h-16 rounded-full bg-slate-900 flex items-center justify-center text-white text-lg font-semibold shrink-0">
            {initials}
          </div>
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-slate-900 truncate">{fullName || 'Your Name'}</h2>
            <p className="text-sm text-slate-500 truncate">{profileEmail}</p>
            {(jobTitle || profile?.role) && (
              <div className="flex items-center gap-2 mt-1.5">
                {jobTitle && (
                  <span className="inline-flex items-center gap-1 text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                    <Briefcase className="h-3 w-3" /> {jobTitle}
                  </span>
                )}
                {profile?.role && (
                  <span className="inline-flex items-center gap-1 text-xs text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full capitalize">
                    <Shield className="h-3 w-3" /> {profile.role}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </SectionCard>

      {/* Personal Information */}
      <SectionCard>
        <SectionHeader icon={User} title="Personal Information" description="Update your name, phone, and job title" />
        <SectionBody>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="full-name" className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">
                Full Name
              </label>
              <input id="full-name" type="text" placeholder="Enter your full name" className={inputClasses} value={fullName} onChange={e => setFullName(e.target.value)} />
            </div>
            <div>
              <label htmlFor="job-title" className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">
                Job Title
              </label>
              <input id="job-title" type="text" placeholder="Enter your job title" className={inputClasses} value={jobTitle} onChange={e => setJobTitle(e.target.value)} />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="profile-phone" className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">
                Phone
              </label>
              <div className="max-w-xs">
                <input id="profile-phone" type="text" placeholder="Enter your phone number" className={inputClasses} value={phone} onChange={e => setPhone(e.target.value)} />
              </div>
            </div>
          </div>
        </SectionBody>
        <SectionFooter>
          <button onClick={handleSaveProfile} disabled={!profileDirty || profileSaving} className="px-4 py-2 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
            {profileSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </SectionFooter>
      </SectionCard>

      {/* Email */}
      <SectionCard>
        <SectionHeader icon={Mail} title="Email Address" description="Change your login email — requires verification" />
        <SectionBody>
          {emailStep === 'display' && (
            <div className="flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{profileEmail}</p>
                <p className="text-xs text-slate-500 mt-0.5">This is the email used to sign in</p>
              </div>
              <button type="button" onClick={() => { setNewEmail(''); setEmailStep('edit'); }} className="px-3 py-2 text-sm font-medium text-slate-700 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors whitespace-nowrap">
                Change Email
              </button>
            </div>
          )}
          {emailStep === 'edit' && (
            <div className="space-y-3 max-w-md">
              <div>
                <label htmlFor="new-email" className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">
                  New Email Address
                </label>
                <input id="new-email" type="email" placeholder="Enter new email address" className={inputClasses} value={newEmail} onChange={e => setNewEmail(e.target.value)} />
              </div>
              <p className="text-xs text-slate-500">A verification code will be sent to the new address.</p>
              <div className="flex gap-2">
                <button type="button" onClick={handleEmailChange} disabled={emailSending} className="px-4 py-2 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                  {emailSending ? 'Sending...' : 'Send Verification Code'}
                </button>
                <button type="button" onClick={handleCancelEmailChange} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors">
                  Cancel
                </button>
              </div>
            </div>
          )}
          {emailStep === 'verify' && (
            <div className="space-y-3 max-w-md">
              <div className="bg-blue-50 text-blue-800 text-sm rounded-lg px-4 py-3">
                Verification code sent to <span className="font-medium">{newEmail}</span>
              </div>
              <div>
                <label htmlFor="verification-code" className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">
                  Verification Code
                </label>
                <input id="verification-code" type="text" placeholder="Enter 6-digit code" className={`${inputClasses} max-w-[200px] tracking-widest`} value={verificationCode} onChange={e => setVerificationCode(e.target.value)} />
              </div>
              <div className="flex items-center gap-3">
                <button type="button" onClick={handleVerifyEmail} disabled={emailVerifying} className="px-4 py-2 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                  {emailVerifying ? 'Verifying...' : 'Verify & Update'}
                </button>
                <button type="button" onClick={handleCancelEmailChange} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors">
                  Cancel
                </button>
                <button type="button" onClick={handleResendCode} className="text-xs text-slate-500 hover:text-slate-700 underline ml-auto">
                  Resend code
                </button>
              </div>
            </div>
          )}
        </SectionBody>
      </SectionCard>

      {/* Password */}
      <SectionCard>
        <SectionHeader icon={Lock} title="Password" description="Update your password to keep your account secure" />
        <SectionBody>
          <div className="space-y-4 max-w-md">
            <div>
              <label htmlFor="current-password" className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">
                Current Password
              </label>
              <input id="current-password" type="password" placeholder="Enter current password" className={inputClasses} value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} />
            </div>
            <div>
              <label htmlFor="new-password" className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">
                New Password
              </label>
              <input id="new-password" type="password" placeholder="Enter new password" className={inputClasses} value={newPassword} onChange={e => setNewPassword(e.target.value)} />
            </div>
            <div>
              <label htmlFor="confirm-new-password" className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">
                Confirm New Password
              </label>
              <input id="confirm-new-password" type="password" placeholder="Confirm new password" className={inputClasses} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} />
            </div>

            {newPassword && (
              <div className="bg-slate-50 rounded-lg p-4 border border-slate-100">
                <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2.5">Requirements</p>
                <ul className="space-y-1.5">
                  <RuleItem passed={passwordRules.length} text="Minimum 8 characters" />
                  <RuleItem passed={passwordRules.uppercase} text="At least one uppercase letter" />
                  <RuleItem passed={passwordRules.lowercase} text="At least one lowercase letter" />
                  <RuleItem passed={passwordRules.number} text="At least one number" />
                  <RuleItem passed={passwordRules.specialChar} text="At least one special character" />
                </ul>
              </div>
            )}
          </div>
        </SectionBody>
        <SectionFooter>
          <button onClick={handleUpdatePassword} disabled={loading || !allRulesPassed || !passwordsMatch} className="px-4 py-2 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
            {loading ? 'Updating...' : 'Update Password'}
          </button>
        </SectionFooter>
      </SectionCard>
    </div>
  );
}

function OrganizationSettings() {
  const { refreshUser } = useAuth();
  const [clinicName, setClinicName] = useState('');
  const [clinicPhone, setClinicPhone] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [clinicEmail, setClinicEmail] = useState('');
  const [clinicTwilioNumber, setClinicTwilioNumber] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const originalValues = useRef({});

  useEffect(() => {
    async function fetchClinicData() {
      try {
        const clinic = await api.getClinic();
        setClinicName(clinic.name || '');
        setClinicPhone(clinic.phone_number || '');
        setAddressLine1(clinic.address_line1 || '');
        setAddressLine2(clinic.address_line2 || '');
        setCity(clinic.city || '');
        setState(clinic.state || '');
        setClinicEmail(clinic.email || '');
        setClinicTwilioNumber(clinic.twilio_number || '');
        originalValues.current = {
          name: clinic.name || '', phone: clinic.phone_number || '',
          addressLine1: clinic.address_line1 || '', addressLine2: clinic.address_line2 || '',
          city: clinic.city || '', state: clinic.state || '', email: clinic.email || ''
        };
      } catch (error) {
        toast.error('Error loading clinic data');
      } finally {
        setLoading(false);
      }
    }
    fetchClinicData();
  }, []);

  const isDirty =
    clinicName !== originalValues.current.name || clinicPhone !== originalValues.current.phone ||
    addressLine1 !== originalValues.current.addressLine1 || addressLine2 !== originalValues.current.addressLine2 ||
    city !== originalValues.current.city || state !== originalValues.current.state ||
    clinicEmail !== originalValues.current.email;

  const validate = () => {
    const newErrors = {};
    if (!clinicName.trim()) newErrors.name = 'Clinic name is required';
    if (clinicEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clinicEmail)) newErrors.email = 'Invalid email format';
    if (clinicPhone && !/^[+]?[\d\s\-().]{7,20}$/.test(clinicPhone)) newErrors.phone = 'Invalid phone number format';
    if (state && state.length > 2) newErrors.state = 'Use a 2-letter state abbreviation';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      await api.updateClinic({
        name: clinicName, phone_number: clinicPhone,
        address_line1: addressLine1, address_line2: addressLine2,
        city, state, email: clinicEmail
      });
      originalValues.current = {
        name: clinicName, phone: clinicPhone, addressLine1, addressLine2, city, state, email: clinicEmail
      };
      toast.success('Clinic settings updated');
      refreshUser();
    } catch (err) {
      toast.error(err.message || 'Failed to update clinic settings');
    } finally {
      setSaving(false);
    }
  };

  const fieldError = (name) => errors[name] ? <p className="text-xs text-red-600 mt-1">{errors[name]}</p> : null;
  const fieldBorder = (name) => errors[name] ? ' border-red-300' : '';

  if (loading) {
    return (
      <div className="space-y-6">
        {[1, 2].map((i) => (
          <div key={i} className="h-48 bg-white rounded-xl border border-slate-200 animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Clinic Info */}
      <SectionCard>
        <SectionHeader icon={Building2} title="Clinic Information" description="Basic details about your clinic" />
        <SectionBody>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="company-name" className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">Clinic Name</label>
              <input id="company-name" type="text" placeholder="Enter clinic name" className={`${inputClasses}${fieldBorder('name')}`} value={clinicName}
                onChange={e => { setClinicName(e.target.value); setErrors(prev => ({ ...prev, name: undefined })); }} />
              {fieldError('name')}
            </div>
            <div>
              <label htmlFor="clinic-phone" className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">Phone Number</label>
              <input id="clinic-phone" type="text" placeholder="Enter phone number" className={`${inputClasses}${fieldBorder('phone')}`} value={clinicPhone}
                onChange={e => { setClinicPhone(e.target.value); setErrors(prev => ({ ...prev, phone: undefined })); }} />
              {fieldError('phone')}
            </div>
            <div>
              <label htmlFor="clinic-email" className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">Email</label>
              <input id="clinic-email" type="email" placeholder="Enter clinic email" className={`${inputClasses}${fieldBorder('email')}`} value={clinicEmail}
                onChange={e => { setClinicEmail(e.target.value); setErrors(prev => ({ ...prev, email: undefined })); }} />
              {fieldError('email')}
            </div>
            <div>
              <label htmlFor="twilio-number" className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">Outgoing Text Number</label>
              <input id="twilio-number" type="text" value={clinicTwilioNumber || 'Not configured'} disabled className={`${inputClasses} bg-slate-50 text-slate-500 cursor-not-allowed`} />
            </div>
          </div>
        </SectionBody>
      </SectionCard>

      {/* Address */}
      <SectionCard>
        <SectionHeader icon={MapPin} title="Address" description="Your clinic's physical address" />
        <SectionBody>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label htmlFor="address-line1" className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">Address Line 1</label>
              <input id="address-line1" type="text" placeholder="Street address" className={inputClasses} value={addressLine1} onChange={e => setAddressLine1(e.target.value)} />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="address-line2" className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">Address Line 2</label>
              <input id="address-line2" type="text" placeholder="Apt, suite, unit, etc. (optional)" className={inputClasses} value={addressLine2} onChange={e => setAddressLine2(e.target.value)} />
            </div>
            <div>
              <label htmlFor="city" className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">City</label>
              <input id="city" type="text" placeholder="City" className={inputClasses} value={city} onChange={e => setCity(e.target.value)} />
            </div>
            <div>
              <label htmlFor="state" className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">State</label>
              <input id="state" type="text" placeholder="e.g. NY" className={`${inputClasses}${fieldBorder('state')}`} value={state}
                onChange={e => { setState(e.target.value); setErrors(prev => ({ ...prev, state: undefined })); }} maxLength={2} />
              {fieldError('state')}
            </div>
          </div>
        </SectionBody>
        <SectionFooter>
          <button onClick={handleSave} disabled={!isDirty || saving} className="px-4 py-2 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </SectionFooter>
      </SectionCard>
    </div>
  );
}

function TeamManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('coordinator');
  const [inviting, setInviting] = useState(false);
  const [removingId, setRemovingId] = useState(null);

  const fetchTeamMembers = async () => {
    try {
      const data = await api.getTeamMembers();
      setUsers(data.members || []);
    } catch (error) {
      toast.error('Error loading team members');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTeamMembers(); }, []);

  const handleInvite = async () => {
    if (!inviteEmail) { toast.error('Please enter an email address'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inviteEmail)) { toast.error('Please enter a valid email address'); return; }
    setInviting(true);
    try {
      await api.inviteTeamMember({ email: inviteEmail, role: inviteRole });
      toast.success(`Invite sent to ${inviteEmail}`);
      setInviteEmail('');
      setInviteRole('coordinator');
      await fetchTeamMembers();
    } catch (err) {
      toast.error(err.message || 'Failed to send invite');
    } finally {
      setInviting(false);
    }
  };

  const handleRemove = async (userId, userEmail) => {
    if (!window.confirm(`Remove ${userEmail} from the team?`)) return;
    setRemovingId(userId);
    try {
      await api.removeTeamMember(userId);
      setUsers(prev => prev.filter(u => u.id !== userId));
      toast.success(`${userEmail} removed from team`);
    } catch (err) {
      toast.error(err.message || 'Failed to remove team member');
    } finally {
      setRemovingId(null);
    }
  };

  const roleColors = {
    admin: 'bg-indigo-50 text-indigo-700',
    coordinator: 'bg-blue-50 text-blue-700',
    viewer: 'bg-slate-100 text-slate-600',
  };

  return (
    <div className="space-y-6">
      {/* Invite */}
      <SectionCard>
        <SectionHeader icon={Mail} title="Invite Team Member" description="Send an invitation to join your clinic" />
        <SectionBody>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <input type="email" placeholder="Enter email address" className={inputClasses} value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} />
            </div>
            <select value={inviteRole} onChange={e => setInviteRole(e.target.value)}
              className="px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-slate-900 focus:border-transparent outline-none bg-white">
              <option value="coordinator">Coordinator</option>
              <option value="admin">Admin</option>
              <option value="viewer">Viewer</option>
            </select>
            <button onClick={handleInvite} disabled={inviting}
              className="px-4 py-2.5 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap">
              {inviting ? 'Sending...' : 'Send Invite'}
            </button>
          </div>
        </SectionBody>
      </SectionCard>

      {/* Members List */}
      <SectionCard>
        <SectionHeader icon={Users} title="Team Members" description={`${users.length} member${users.length !== 1 ? 's' : ''}`} />
        {loading ? (
          <div className="px-6 py-4 space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-14 bg-slate-50 rounded-lg animate-pulse" />
            ))}
          </div>
        ) : users.length > 0 ? (
          <div className="divide-y divide-slate-100">
            {users.map((user) => {
              const memberInitials = (user.full_name || user.email || '?')
                .split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
              return (
                <div key={user.id} className="px-6 py-4 flex items-center gap-4 hover:bg-slate-50/70 transition-colors">
                  <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center text-sm font-medium text-slate-600 shrink-0">
                    {memberInitials}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">
                      {user.full_name || user.email}
                    </p>
                    {user.full_name && (
                      <p className="text-xs text-slate-500 truncate">{user.email}</p>
                    )}
                  </div>
                  {user.role && (
                    <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full capitalize ${roleColors[user.role] || roleColors.viewer}`}>
                      {user.role}
                    </span>
                  )}
                  <button onClick={() => handleRemove(user.id, user.email)} disabled={removingId === user.id}
                    className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 shrink-0" title="Remove member">
                    {removingId === user.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="px-6 py-12 text-center">
            <div className="w-12 h-12 rounded-xl bg-slate-100 mx-auto mb-3 flex items-center justify-center">
              <Users className="h-5 w-5 text-slate-400" />
            </div>
            <p className="text-sm font-medium text-slate-900">No team members</p>
            <p className="text-xs text-slate-500 mt-1">Invite someone above to get started</p>
          </div>
        )}
      </SectionCard>
    </div>
  );
}

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function ReportsSettings({ clinicId }) {
  const [reportMonth, setReportMonth] = useState(new Date().getMonth() + 1);
  const [reportYear, setReportYear] = useState(new Date().getFullYear());
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [reportEmail, setReportEmail] = useState('');
  const [reportSending, setReportSending] = useState(false);
  const [reportError, setReportError] = useState(null);
  const [reportSuccess, setReportSuccess] = useState(false);

  useEffect(() => {
    if (!clinicId) return;
    async function fetchStats() {
      setStatsLoading(true);
      try {
        const startOfMonth = `${reportYear}-${String(reportMonth).padStart(2, '0')}-01T00:00:00`;
        const endDate = new Date(reportYear, reportMonth, 0);
        const endOfMonth = `${reportYear}-${String(reportMonth).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}T23:59:59`;

        const patientsData = await api.getPatients();
        const allPatients = patientsData.patients || patientsData || [];

        const patientsIdentified = allPatients.filter(
          p => p.created_at && p.created_at <= endOfMonth
        ).length;

        const patientsReturned = allPatients.filter(
          p => ['responded', 'done', 'closed'].includes(p.status) &&
               p.lastTouch && p.lastTouch >= startOfMonth && p.lastTouch <= endOfMonth
        ).length;

        let outreachSent = 0;
        try {
          const reportStats = await api.getReportStats(reportMonth, reportYear);
          outreachSent = reportStats.outreach_sent || 0;
        } catch (e) {
          // Fall back to 0 if endpoint unavailable
        }
        const estimatedRevenue = patientsReturned * 150;

        setStats({ patientsIdentified, outreachSent, patientsReturned, estimatedRevenue });
      } catch (err) {
        setStats({ patientsIdentified: 0, outreachSent: 0, patientsReturned: 0, estimatedRevenue: 0 });
      } finally {
        setStatsLoading(false);
      }
    }
    fetchStats();
  }, [clinicId, reportMonth, reportYear]);

  const handleSendReport = async () => {
    if (!reportEmail.trim()) { setReportError('Please enter a recipient email.'); return; }
    setReportError(null);
    setReportSuccess(false);
    setReportSending(true);
    try {
      await api.sendSummaryReport({ recipient_email: reportEmail.trim(), period_month: reportMonth, period_year: reportYear });
      setReportSuccess(true);
    } catch (err) {
      setReportError(err.message || 'Something went wrong.');
    } finally {
      setReportSending(false);
    }
  };

  const allZero = stats && stats.patientsIdentified === 0 && stats.outreachSent === 0 && stats.patientsReturned === 0;

  return (
    <div className="space-y-6">
      {/* Report Card */}
      <SectionCard>
        {/* Branded Header */}
        <div className="bg-gradient-to-r from-slate-900 to-slate-700 px-6 py-5">
          <h3 className="text-white text-lg font-semibold">Outreach Summary</h3>
          <div className="flex items-center gap-3 mt-3">
            <select value={reportMonth}
              onChange={e => { setReportMonth(Number(e.target.value)); setReportError(null); setReportSuccess(false); }}
              className="bg-white/15 text-white text-sm rounded-lg px-3 py-1.5 border border-white/20 outline-none [&>option]:text-slate-900 hover:bg-white/25 transition-colors">
              {MONTH_NAMES.map((name, i) => <option key={i + 1} value={i + 1}>{name}</option>)}
            </select>
            <select value={reportYear}
              onChange={e => { setReportYear(Number(e.target.value)); setReportError(null); setReportSuccess(false); }}
              className="bg-white/15 text-white text-sm rounded-lg px-3 py-1.5 border border-white/20 outline-none [&>option]:text-slate-900 hover:bg-white/25 transition-colors">
              <option value={new Date().getFullYear()}>{new Date().getFullYear()}</option>
              <option value={new Date().getFullYear() - 1}>{new Date().getFullYear() - 1}</option>
            </select>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="p-6">
          {statsLoading ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => <div key={i} className="h-24 bg-slate-50 rounded-lg animate-pulse" />)}
            </div>
          ) : allZero ? (
            <div className="text-center py-10">
              <div className="w-12 h-12 rounded-xl bg-slate-100 mx-auto mb-3 flex items-center justify-center">
                <BarChart3 className="h-5 w-5 text-slate-400" />
              </div>
              <p className="text-sm font-medium text-slate-900">No activity</p>
              <p className="text-xs text-slate-500 mt-1">No data for the selected period</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="rounded-lg border border-slate-200 p-4">
                <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Patients Identified</p>
                <p className="text-2xl font-bold text-slate-900 mt-2">{stats.patientsIdentified.toLocaleString()}</p>
              </div>
              <div className="rounded-lg border border-slate-200 p-4">
                <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Outreach Sent</p>
                <p className="text-2xl font-bold text-slate-900 mt-2">{stats.outreachSent.toLocaleString()}</p>
              </div>
              <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-4">
                <p className="text-[11px] font-semibold text-emerald-600 uppercase tracking-wider">Patients Returned</p>
                <p className="text-2xl font-bold text-emerald-800 mt-2">{stats.patientsReturned.toLocaleString()}</p>
              </div>
              <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-4">
                <p className="text-[11px] font-semibold text-emerald-600 uppercase tracking-wider">Est. Revenue</p>
                <p className="text-2xl font-bold text-emerald-800 mt-2">${stats.estimatedRevenue.toLocaleString()}</p>
              </div>
            </div>
          )}
        </div>

        {/* Email CTA */}
        <div className="border-t border-slate-100 px-6 py-4 bg-slate-50/50">
          <div className="flex items-center gap-3">
            <input type="email" placeholder="Recipient email" className={`${inputClasses} flex-1`} value={reportEmail}
              onChange={e => { setReportEmail(e.target.value); setReportError(null); setReportSuccess(false); }} />
            <button onClick={handleSendReport} disabled={reportSending}
              className="inline-flex items-center px-5 py-2.5 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap">
              {reportSending ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending...</>
              ) : (
                <><Send className="h-4 w-4 mr-2" />Email Report</>
              )}
            </button>
          </div>
          {reportError && <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2 mt-3">{reportError}</p>}
          {reportSuccess && <p className="text-sm text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2 mt-3">Report sent successfully.</p>}
        </div>
      </SectionCard>
    </div>
  );
}
